import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { Dialog, Icon, TextField, Layout, Field, Banner } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'

import { useUpdateShipmentTractorNumber } from '../../../ui/hooks/useUpdateShipmentTractorNumber'

type TruckFormProps = {
  readonly shipmentUUID: string
  readonly onCancel: () => void
  readonly onSave: () => void
}

const validationSchema = Yup.object().shape({
  tractorNumber: Yup.string().required('Required').max(255, 'Truck number is too long'),
})

export function TruckForm({ shipmentUUID, onCancel, onSave }: TruckFormProps) {
  const [tractorNumber, setTractorNumber] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { updateTractor, requestStatus } = useUpdateShipmentTractorNumber()

  useEffect(() => {
    if (requestStatus === RequestStatus.Error) {
      toast.error('Failed to assign the truck')
    }
  }, [requestStatus])

  useEffect(() => {
    if (requestStatus === RequestStatus.Done) {
      onSave()
    }
  }, [onSave, requestStatus])

  async function handleSave() {
    setError(null)

    try {
      validationSchema.validateSync({ tractorNumber })

      await updateTractor({
        shipmentUUID,
        tractorNumber: tractorNumber as string,
      })
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        setError(error.errors[0])
      }
    }
  }

  return (
    <>
      <Dialog.Body>
        <Layout.Stack gap="spacing-4">
          {requestStatus === RequestStatus.Error && (
            <Banner variant="danger">
              <Banner.Title>Failed to create the truck</Banner.Title>
              <Banner.Description>
                Please review the information below and try again.
              </Banner.Description>
              <Banner.Actions>
                <Banner.ActionPrimary onClick={handleSave}>Retry</Banner.ActionPrimary>
              </Banner.Actions>
            </Banner>
          )}

          <Field status={error ? 'danger' : 'default'} required>
            <Field.Label id="truck-number-label">Truck number</Field.Label>
            <TextField
              aria-labelledby="truck-number-label"
              placeholder="e.g. ABC-1245"
              onInput={event => {
                setTractorNumber((event.target as HTMLInputElement).value)
              }}
            />
            <Field.Hint>{error}</Field.Hint>
          </Field>
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionTertiary
          onClick={onCancel}
          leading={<Icon name="back" size="16px" color="color-primary-100" />}
        >
          Back
        </Dialog.ActionTertiary>
        <Dialog.ActionPrimary
          onClick={handleSave}
          loading={requestStatus === RequestStatus.Pending}
        >
          Create and confirm
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </>
  )
}
